'use client';

import clsx from 'clsx';
import { usePathname, useSearchParams } from 'next/navigation';

import { useHandleQuickLink, type UrlTriggerEvent } from '../../hooks/UseNavigation';
import { containsPattern } from '../../utils/ContainsPattern';

import styles from './NavQuickLinkBannerItem.module.scss';

export interface NavQuickLinkBannerItemProps {
  /** System ID */
  sys?: { id: string };
  /** Navigation URL */
  url?: string | null;
  /** URL target (_self, _blank) */
  urlTarget?: string | null;
  /** Display text */
  urlText?: string | null;
  /** URL trigger event (opens modal instead of navigating) */
  urlTrigger?: UrlTriggerEvent | null;
  /** Optional className */
  className?: string;
  /** Test ID */
  'data-testid'?: string;
}

/**
 * NavQuickLinkBannerItem - Banner text link
 *
 * A simple text link designed for dark banner backgrounds.
 * Used for section navigation within articles, stories, etc.
 *
 * Features:
 * - Light text on dark background
 * - Active state with underline
 * - URL pattern matching for active detection
 */
export function NavQuickLinkBannerItem({
  className,
  url,
  urlTarget,
  urlText,
  urlTrigger,
  'data-testid': testId,
}: NavQuickLinkBannerItemProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { handleQuickLink } = useHandleQuickLink(url ?? '/', urlTarget ?? '_self', urlTrigger);

  // Determine if this link is active
  const slug = searchParams.get('slug');
  const isContains = containsPattern(pathname, url);
  const isActive = slug?.includes(url ?? '') || pathname?.includes(url ?? '') || isContains;

  return (
    <button
      className={clsx(styles.wrapper, isActive && styles.active, className)}
      onClick={handleQuickLink}
      data-testid={testId ?? 'NavQuickLinkBannerItemWrapper'}
      type="button"
    >
      {urlText}
    </button>
  );
}

NavQuickLinkBannerItem.displayName = 'NavQuickLinkBannerItem';
